import {useEffect,useState} from 'react';
import {ProfileAvatar} from './ProfileAvatar';
import {GuestWaivers} from './GuestWaivers';

type Account={actorId:string;actorKind:'TEAM_MEMBER'|'CUSTOMER';
  displayName:string;email:string|null;lastLoginAt?:string|null};
type OwnVisit={appointmentId:string;locationName:string;locationTimezone:string;
  serviceName:string;startsAt:string;status:string};
type OwnCredit={creditId:string;serviceCode:string;serviceName:string;
  remainingUses:number;expiresAt:string|null};

export function MeWorkspace({onBook,onStaff,locationCode}:{
  onBook:()=>void;onStaff?:()=>void;locationCode:string;
}){
  const [account,setAccount]=useState<Account|null>(null);
  const [activeTab,setActiveTab]=useState<'appointments'|'credits'>('appointments');
  const [credits,setCredits]=useState<{status:'loading'|'ready'|'error';items:OwnCredit[]}>({
    status:'loading',items:[],
  });
  const [status,setStatus]=useState<'loading'|'ready'|'error'>('loading');
  const [visits,setVisits]=useState<{status:'loading'|'ready'|'error';items:OwnVisit[]}>(
    {status:'loading',items:[]});
  // A server-owned keyset cursor allows complete past-history browsing
  // without guessing time windows or storing guest visits in browser storage.
  const [historyCursor,setHistoryCursor]=useState<{
    startsAt:string;appointmentId:string}|null>(null);
  const [historyRetry,setHistoryRetry]=useState(0);
  const [pastVisits,setPastVisits]=useState<{status:'loading'|'ready'|'error';
    items:OwnVisit[];hasMore:boolean;
    nextCursor:{startsAt:string;appointmentId:string}|null}>({
      status:'loading',items:[],hasMore:false,nextCursor:null,
    });
  useEffect(()=>{
    const controller=new AbortController();
    void fetch('/api/v1/me',{credentials:'same-origin',cache:'no-store',
      signal:controller.signal}).then(async response=>{
      if(!response.ok)throw Error('PROFILE_UNAVAILABLE');
      const data=await response.json() as {ok:boolean;data?:Account};
      if(!data.ok||!data.data)throw Error('PROFILE_UNAVAILABLE');
      if(controller.signal.aborted)return;
      setAccount(data.data);setStatus('ready');
    }).catch(()=>{if(!controller.signal.aborted)setStatus('error');});
    return ()=>controller.abort();
  },[]);
  useEffect(()=>{
    const controller=new AbortController();
    const start=Math.floor(Date.now()/60000)*60000;
    const query=new URLSearchParams({
      from:new Date(start).toISOString(),to:new Date(start+90*86400000).toISOString(),
    });
    void fetch('/api/v1/guest/appointments?'+query,{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw new Error('APPOINTMENTS_UNAVAILABLE');
      const body=await response.json() as {ok:boolean;data?:{appointments:OwnVisit[]}};
      const appointments=body.data?.appointments;
      if(!body.ok||!Array.isArray(appointments))
        throw new Error('APPOINTMENTS_UNAVAILABLE');
      if(!controller.signal.aborted)setVisits({status:'ready',items:appointments});
    }).catch(()=>{if(!controller.signal.aborted)setVisits({status:'error',items:[]});});
    return ()=>controller.abort();
  },[]);
  useEffect(()=>{
    const controller=new AbortController();
    const params=new URLSearchParams({limit:'20'});
    if(historyCursor){
      params.set('beforeStart',historyCursor.startsAt);
      params.set('beforeId',historyCursor.appointmentId);
    }
    setPastVisits(previous=>({...previous,status:'loading'}));
    void fetch('/api/v1/guest/appointments/history?'+params,{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('APPOINTMENTS_UNAVAILABLE');
      const body=await response.json() as {ok:boolean;data?:{
        appointments:OwnVisit[];hasMore:boolean;
        nextCursor:{startsAt:string;appointmentId:string}|null;
      }};
      const data=body.data;
      if(!body.ok||!data||!Array.isArray(data.appointments)||
        typeof data.hasMore!=='boolean'||(data.hasMore&&!data.nextCursor))
        throw Error('APPOINTMENTS_UNAVAILABLE');
      if(controller.signal.aborted)return;
      setPastVisits(previous=>{
        const records=historyCursor
          ?[...previous.items,...data.appointments]:data.appointments;
        const unique=new Map(records.map(record=>[record.appointmentId,record]));
        return {status:'ready',items:[...unique.values()],
          hasMore:data.hasMore,nextCursor:data.nextCursor};
      });
    }).catch(()=>{if(!controller.signal.aborted)
      setPastVisits(previous=>({...previous,status:'error'}));});
    return ()=>controller.abort();
  },[historyCursor,historyRetry]);
  useEffect(()=>{
    if(account?.actorKind!=='CUSTOMER')return;
    const controller=new AbortController();
    void fetch('/api/v1/guest/credits',{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('CREDITS_UNAVAILABLE');
      const body=await response.json() as {ok:boolean;data?:{credits:OwnCredit[]}};
      if(!body.ok||!Array.isArray(body.data?.credits))
        throw Error('CREDITS_UNAVAILABLE');
      if(!controller.signal.aborted)setCredits({status:'ready',items:body.data.credits});
    }).catch(()=>{if(!controller.signal.aborted)
      setCredits({status:'error',items:[]});});
    return ()=>controller.abort();
  },[account?.actorKind]);
  if(status==='loading')return <section className="ecfs-me-loading" role="status">Loading profile…</section>;
  if(status==='error'||!account)return <section className="ecfs-me-loading"
    role="alert">Profile is unavailable.</section>;
  const names=account.displayName.trim().split(/\s+/);
  return <article className="ecfs-record-profile ecfs-me-profile" aria-label="My Profile">
    <aside className="ecfs-record-sidebar" aria-label="My profile sidebar">
      <ProfileAvatar actorId={account.actorId} displayName={account.displayName}
        lastLoginAt={account.lastLoginAt}/>
      <div className="ecfs-helm-contact-fields">
        <div className="ecfs-helm-contact-field">{names[0]}</div>
        {names.length>1&&<div className="ecfs-helm-contact-field">
          {names.slice(1).join(' ')}</div>}
        {account.email&&<div className="ecfs-helm-contact-field">
          <a href={'mailto:'+account.email}>{account.email}</a></div>}
      </div>
    </aside>
    <div className="ecfs-record-main">
      <div className="ecfs-record-heading"><h1>{account.displayName}</h1></div>
      <nav className="ecfs-me-actions" aria-label="Account navigation">
        <button type="button" onClick={onBook}>Booking</button>
        {onStaff&&<button type="button" onClick={onStaff}>Schedule</button>}
      </nav>
      <div className="ecfs-record-tabs" role="tablist" aria-label="My Account records">
        <button type="button" role="tab" aria-selected={activeTab==='appointments'}
          onClick={()=>setActiveTab('appointments')}>Appointments</button>
        {account.actorKind==='CUSTOMER'&&<button type="button" role="tab"
          aria-selected={activeTab==='credits'}
          onClick={()=>setActiveTab('credits')}>Service Credits</button>}
      </div>
      {activeTab==='appointments'&&<section className="ecfs-record-tab-content"
        aria-label="My upcoming appointments">
        <h2>Upcoming Appointments</h2>
        {visits.status==='loading'&&<p role="status">Loading upcoming visits…</p>}
        {visits.status==='error'&&<p role="status">
          Upcoming appointments are temporarily unavailable.</p>}
        {visits.status==='ready'&&(visits.items.length>0
          ?<div className="ecfs-record-table-scroll">
            <table className="ecfs-record-table">
              <thead><tr><th>Beginning</th><th>Service</th><th>Location</th><th>Status</th></tr></thead>
              <tbody>{visits.items.map(visit=><tr key={visit.appointmentId}>
                <td>{new Intl.DateTimeFormat('en-US',{
                  dateStyle:'medium',timeStyle:'short',timeZone:visit.locationTimezone,
                }).format(new Date(visit.startsAt))}</td>
                <td>{visit.serviceName}</td><td>{visit.locationName}</td>
                <td>{visit.status}</td>
              </tr>)}</tbody>
            </table>
          </div>
          :<p role="status">No upcoming appointments in the next 90 days.</p>)}
      </section>}
      {activeTab==='appointments'&&<section className="ecfs-record-tab-content"
        aria-label="My past appointments">
        <h2>Past Appointments</h2>
        {pastVisits.status==='loading'&&<p role="status">Loading past appointments…</p>}
        {pastVisits.status==='error'&&<p role="alert">Past appointments are temporarily
          unavailable. Earlier entries have not been removed.</p>}
        {pastVisits.items.length>0&&<div className="ecfs-record-table-scroll">
          <table className="ecfs-record-table">
            <thead><tr><th>Beginning</th><th>Service</th><th>Location</th><th>Status</th></tr></thead>
            <tbody>{pastVisits.items.map(visit=><tr key={visit.appointmentId}>
              <td>{new Intl.DateTimeFormat('en-US',{
                dateStyle:'medium',timeStyle:'short',timeZone:visit.locationTimezone,
              }).format(new Date(visit.startsAt))}</td>
              <td>{visit.serviceName}</td><td>{visit.locationName}</td>
              <td>{visit.status}</td>
            </tr>)}</tbody>
          </table>
        </div>}
        {pastVisits.status==='ready'&&pastVisits.items.length===0&&
          <p role="status">No past appointments on this account.</p>}
        {(pastVisits.hasMore||pastVisits.status==='error')&&
          <button type="button" disabled={pastVisits.status==='loading'}
            onClick={()=>pastVisits.status==='error'
              ?setHistoryRetry(retry=>retry+1)
              :setHistoryCursor(pastVisits.nextCursor)}>
            {pastVisits.status==='error'?'Retry past appointments':'Load older appointments'}
          </button>}
      </section>}
      {activeTab==='credits'&&account.actorKind==='CUSTOMER'&&
        <section className="ecfs-record-tab-content" aria-label="My service credits">
          <h2>Service Credits</h2>
          {credits.status==='loading'&&<p role="status">Loading service credits…</p>}
          {credits.status==='error'&&<p role="alert">
            Your service credits are temporarily unavailable.</p>}
          {credits.status==='ready'&&(credits.items.length?
            <div className="ecfs-record-table-scroll">
              <table className="ecfs-record-table">
                <thead><tr><th>Service</th><th>Remaining</th><th>Expires</th></tr></thead>
                <tbody>{credits.items.map(credit=><tr key={credit.creditId}>
                  <td>{credit.serviceName}</td>
                  <td>{credit.remainingUses}</td>
                  <td>{credit.expiresAt?new Intl.DateTimeFormat('en-US',{
                    dateStyle:'medium',timeZone:'UTC',
                  }).format(new Date(credit.expiresAt)):'No expiration'}</td>
                </tr>)}</tbody>
              </table>
            </div>
            :<p role="status">No unused, unexpired service credits are currently available
              on this account.</p>)}
        </section>}
      <GuestWaivers locationCode={locationCode}/>
    </div>
  </article>;
}
